import React, { useState, useEffect } from 'react';
import './Settings.css';
import { ApiService } from '../../services/api';

interface QualityAttribute {
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
  enabled: boolean;
}

interface Metadata {
  id: string;
  name: string;
}

const Settings: React.FC = () => {
  const [selectedMetadata, setSelectedMetadata] = useState<string>('');
  const [metadataList, setMetadataList] = useState<Metadata[]>([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rangeErrors, setRangeErrors] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [attributes, setAttributes] = useState<QualityAttribute[]>([
    { id: 'poor', name: 'Poor', minValue: 0, maxValue: 20, enabled: true },
    { id: 'fair', name: 'Fair', minValue: 20, maxValue: 40, enabled: true },
    { id: 'good', name: 'Good', minValue: 40, maxValue: 60, enabled: true },
    { id: 'veryGood', name: 'Very Good', minValue: 60, maxValue: 80, enabled: true },
    { id: 'excellent', name: 'Excellent', minValue: 80, maxValue: 100, enabled: true }
  ]);

  // Fetch metadata list on component mount
  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const response = await ApiService.getMetadataFiles(0, 100);
      const transformedData: Metadata[] = response.data?.map((file: any) => ({
        id: file.id.toString(),
        name: file.file_name
      })) || [];
      setMetadataList(transformedData);
    } catch (error) {
      console.error('Error fetching metadata:', error);
      setMetadataList([]);
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const handleMetadataChange = async (metadataId: string) => {
    setSelectedMetadata(metadataId);
    setSaveMessage('');
    
    if (metadataId) {
      // Load existing settings for this metadata file
      await loadMetadataSettings(parseInt(metadataId));
    } else {
      // Reset to default values
      resetToDefaultAttributes();
    }
  };

  const resetToDefaultAttributes = () => {
    setAttributes([
      { id: 'poor', name: 'Poor', minValue: 0, maxValue: 20, enabled: true },
      { id: 'fair', name: 'Fair', minValue: 20, maxValue: 40, enabled: true },
      { id: 'good', name: 'Good', minValue: 40, maxValue: 60, enabled: true },
      { id: 'veryGood', name: 'Very Good', minValue: 60, maxValue: 80, enabled: true },
      { id: 'excellent', name: 'Excellent', minValue: 80, maxValue: 100, enabled: true }
    ]);
  };

  const loadMetadataSettings = async (metadataFileId: number) => {
    setIsLoadingSettings(true);
    try {
      const settingsData = await ApiService.getMetadataSettingsAttributes(metadataFileId);
      
      // Transform API response to component format
      const transformedAttributes: QualityAttribute[] = [
        { id: 'poor', name: 'Poor', minValue: settingsData.Poor?.min || 0, maxValue: settingsData.Poor?.max || 20, enabled: true },
        { id: 'fair', name: 'Fair', minValue: settingsData.Fair?.min || 20, maxValue: settingsData.Fair?.max || 40, enabled: true },
        { id: 'good', name: 'Good', minValue: settingsData.Good?.min || 40, maxValue: settingsData.Good?.max || 60, enabled: true },
        { id: 'veryGood', name: 'Very Good', minValue: settingsData['Very Good']?.min || 60, maxValue: settingsData['Very Good']?.max || 80, enabled: true },
        { id: 'excellent', name: 'Excellent', minValue: settingsData.Excellent?.min || 80, maxValue: settingsData.Excellent?.max || 100, enabled: true }
      ];
      
      setAttributes(transformedAttributes);
    } catch (error) {
      console.error('Error loading metadata settings:', error);
      // If settings don't exist, use default values
      resetToDefaultAttributes();
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Validate sequential ranges
  const validateRanges = (updatedAttributes: QualityAttribute[]) => {
    const errors: Record<string, string> = {};
    
    updatedAttributes.forEach((attr, index) => {
      // Check if min >= max
      if (attr.minValue >= attr.maxValue) {
        errors[attr.id] = 'Min value must be less than max value';
      }
      
      // Check if previous attribute is properly set (except for first)
      if (index > 0) {
        const prevAttr = updatedAttributes[index - 1];
        if (attr.minValue !== prevAttr.maxValue) {
          errors[attr.id] = `Must start where ${prevAttr.name} ends`;
        }
      }
    });
    
    setRangeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAttributeChange = (id: string, maxValue: number) => {
    const currentIndex = attributes.findIndex(attr => attr.id === id);
    if (currentIndex === -1) return;
    
    const updatedAttributes = [...attributes];
    updatedAttributes[currentIndex] = { 
      ...updatedAttributes[currentIndex], 
      maxValue: maxValue 
    };
    
    // Update ALL subsequent attributes when any bar limit changes
    for (let i = currentIndex + 1; i < attributes.length; i++) {
      // Set minValue to previous attribute's maxValue
      updatedAttributes[i] = {
        ...updatedAttributes[i],
        minValue: updatedAttributes[i - 1].maxValue
      };
      
      // If current change pushes into next attribute's range, move that attribute up
      if (updatedAttributes[i - 1].maxValue >= updatedAttributes[i].maxValue) {
        updatedAttributes[i] = {
          ...updatedAttributes[i],
          maxValue: Math.min(100, updatedAttributes[i - 1].maxValue + 5)
        };
      }
    }
    
    // Ensure no attribute exceeds 100 and cascade adjustments
    for (let i = attributes.length - 1; i >= 0; i--) {
      if (updatedAttributes[i].maxValue > 100) {
        updatedAttributes[i] = {
          ...updatedAttributes[i],
          maxValue: 100
        };
        
        // Adjust all previous attributes if needed
        for (let j = i - 1; j >= 0; j--) {
          if (updatedAttributes[j].maxValue > updatedAttributes[j + 1].minValue) {
            updatedAttributes[j] = {
              ...updatedAttributes[j],
              maxValue: Math.max(updatedAttributes[j].minValue + 1, updatedAttributes[j + 1].minValue - 1)
            };
          }
        }
      }
    }
    
    setAttributes(updatedAttributes);
    setTimeout(() => validateRanges(updatedAttributes), 0);
  };

  const handleSliderStart = (e: React.MouseEvent, attributeId: string) => {
    e.preventDefault();
    
    const barTrack = (e.target as HTMLElement).closest('.bar-track') || 
                     (e.target as HTMLElement).closest('.equalizer-bar');
    if (!barTrack) return;
    
    const barRect = barTrack.getBoundingClientRect();
    const barHeight = barRect.height;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const mouseY = moveEvent.clientY;
      const relativeY = mouseY - barRect.top;
      const percentage = Math.max(0, Math.min(100, 100 - (relativeY / barHeight) * 100));
      
      const currentIndex = attributes.findIndex(attr => attr.id === attributeId);
      if (currentIndex === -1) return;
      
      const minValue = currentIndex === 0 ? 0 : attributes[currentIndex - 1].maxValue;
      const newMaxValue = Math.max(minValue + 1, Math.round(percentage));
      
      handleAttributeChange(attributeId, newMaxValue);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSave = async () => {
    if (!selectedMetadata) {
      alert('Please select metadata before saving settings.');
      return;
    }

    const enabledAttributes = attributes; // All attributes are now mandatory
    
    // Check if all ranges are properly configured
    if (attributes.some(attr => attr.minValue >= attr.maxValue)) {
      alert('Please ensure all attributes have valid ranges.');
      return;
    }

    // Validate ranges before saving
    if (!validateRanges(attributes)) {
      alert('Please fix range conflicts before saving.');
      return;
    }

    const settingsData = {
      metadataId: selectedMetadata,
      attributes: enabledAttributes.reduce((acc, attr) => {
        acc[attr.name] = { min: attr.minValue, max: attr.maxValue };
        return acc;
      }, {} as Record<string, { min: number; max: number }>)
    };

    setIsSaving(true);
    setSaveMessage('');

    try {
      const response = await ApiService.saveMetadataSettings(settingsData);
      
      // Update the console log to use 'settings_saved' as requested
      const responseData = {
        settings_saved: response.settings_saved,
        message: response.message,
        id: response.id
      };
      
      console.log('settings_saved:', JSON.stringify(responseData, null, 2));
      setSaveMessage('Settings saved successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
      
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="text-2xl font-bold mb-1 text-gray-800">Settings</h1>
        <p className="settings-description">Configure quality attribute thresholds</p>
      </div>

      <div className="settings-content">
        {/* Metadata Selection Section */}
        <div className="metadata-section">
          <h2 className="section-title">Select Metadata</h2>
          <p className="section-description">
            Choose a metadata to configure quality attributes
          </p>
          
          <div className="metadata-dropdown-container">
            <select
              value={selectedMetadata}
              onChange={(e) => handleMetadataChange(e.target.value)}
              disabled={isLoadingMetadata}
              className="metadata-dropdown"
            >
              <option value="">
                {isLoadingMetadata ? 'Loading metadata...' : 'Select a metadata'}
              </option>
              {metadataList.map((metadata) => (
                <option key={metadata.id} value={metadata.id}>
                  {metadata.name} (ID: {metadata.id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quality Attributes Section */}
        <div className={`attributes-section ${!selectedMetadata ? 'disabled-section' : ''}`}>
          <h2 className="section-title">Quality Attributes</h2>
          <p className="section-description">
            {selectedMetadata 
              ? (isLoadingSettings ? 'Loading existing settings...' : 'Configure min and max threshold ranges (0-100)')
              : 'Please select a metadata first to enable this section'
            }
          </p>

          {/* Save Message */}
          {saveMessage && (
            <div className={`save-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
              {saveMessage}
            </div>
          )}

          <div className="equalizer-container">
            {attributes.map((attribute) => (
              <div key={attribute.id} className={`equalizer-channel ${!selectedMetadata ? 'disabled' : ''} ${rangeErrors[attribute.id] ? 'error' : ''}`}>
                <div className="channel-header">
                  <h3 className="channel-name">{attribute.name}</h3>
                </div>

                {rangeErrors[attribute.id] && (
                  <div className="error-message">
                    {rangeErrors[attribute.id]}
                  </div>
                )}

                <div className="equalizer-bar-container">
                  <div className="range-scale">
                    <span className="scale-mark">100</span>
                    <span className="scale-mark">75</span>
                    <span className="scale-mark">50</span>
                    <span className="scale-mark">25</span>
                    <span className="scale-mark">0</span>
                  </div>
                  
                  <div 
                    className="equalizer-bar"
                    onMouseDown={(e) => handleSliderStart(e, attribute.id)}
                  >
                    <div className="bar-track">
                      <div 
                        className={`bar-fill ${rangeErrors[attribute.id] ? 'error' : ''}`}
                        style={{
                          height: `${attribute.maxValue}%`
                        }}
                      />
                      
                      <div 
                        className="volume-handle"
                        style={{ bottom: `${attribute.maxValue}%` }}
                        title={`${attribute.name}: ${attribute.maxValue}`}
                      />
                    </div>
                  </div>
                  
                  <div className="range-labels">
                    <span className="max-label">{attribute.maxValue}</span>
                    <span className="min-label">{attribute.minValue}</span>
                  </div>
                  
                </div>

                <div className="channel-status">
                  {attribute.enabled ? (
                    <span className="status-active">Active</span>
                  ) : (
                    <span className="status-disabled">Disabled</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="settings-actions">
            <button
              onClick={handleSave}
              className="save-button"
              disabled={!selectedMetadata || isSaving || isLoadingSettings}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
